import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Lock, Save, ArrowLeft, CheckCircle, Info, Calendar } from 'lucide-react'; // Adicionado Info e Calendar
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth, api } from '../contexts/AuthContext';
import NotificationPreferences from '../components/NotificationPreferences';
import { useLanguage } from '../i18n';
import { toast } from 'sonner';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const ProfilePage = () => {
  const { user, updateUser, loading: authLoading } = useAuth(); // Adicionado authLoading
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [showIncompleteWarning, setShowIncompleteWarning] = useState(false); // Estado para o aviso
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    birthdate: '', // Novo campo
  });
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        birthdate: user.birthdate || '', // Novo campo
      });
      
      // Ativa o aviso se faltar o telefone
      if (!user.phone) {
        setShowIncompleteWarning(true);
      }
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await api.put('/auth/profile', {
        name: formData.name,
        phone: formData.phone,
        birthdate: formData.birthdate, // Enviando nova info
      });
      
      if (updateUser) {
        updateUser(response.data);
      }
      
      toast.success(language === 'pt' ? 'Perfil atualizado com sucesso!' : 'Profile updated successfully!');
      
      // Se ele preencheu o telefone agora, podemos mandar de volta para o Dashboard
      if (response.data.phone) {
        setShowIncompleteWarning(false);
        setTimeout(() => navigate('/dashboard'), 1500);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || (language === 'pt' ? 'Erro ao atualizar perfil' : 'Error updating profile'));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(language === 'pt' ? 'As senhas não coincidem' : 'Passwords do not match');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error(language === 'pt' ? 'A senha deve ter pelo menos 6 caracteres' : 'Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await api.put('/auth/change-password', {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
      });
      toast.success(language === 'pt' ? 'Senha alterada com sucesso!' : 'Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || (language === 'pt' ? 'Erro ao alterar senha' : 'Error changing password'));
    } finally {
      setLoading(false);
    }
  };

  const t = {
    pt: {
      title: 'Meu Perfil',
      subtitle: 'Atualize as suas informações pessoais',
      incompleteTitle: 'Conclua o seu registo',
      incompleteMsg: 'Precisamos do seu telemóvel para que possa aceder ao Dashboard e gerir marcações.',
      name: 'Nome Completo',
      email: 'Email',
      phone: 'Telemóvel',
      birthdate: 'Data de Nascimento',
      phonePlaceholder: '+351 912 345 678',
      phoneHelper: 'Este número será usado para notificações via WhatsApp',
      save: 'Guardar Alterações',
      changePassword: 'Alterar Senha',
      currentPassword: 'Senha Atual',
      newPassword: 'Nova Senha',
      confirmPassword: 'Confirmar Nova Senha',
      cancel: 'Cancelar',
      back: 'Voltar',
      saving: 'A guardar...',
    },
    en: {
      title: 'My Profile',
      subtitle: 'Update your personal information',
      incompleteTitle: 'Complete your registration',
      incompleteMsg: 'We need your phone number so you can access the Dashboard and manage bookings.',
      name: 'Full Name',
      email: 'Email',
      phone: 'Phone',
      birthdate: 'Birthdate',
      phonePlaceholder: '+351 912 345 678',
      phoneHelper: 'This number will be used for WhatsApp notifications',
      save: 'Save Changes',
      changePassword: 'Change Password',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      confirmPassword: 'Confirm New Password',
      cancel: 'Cancel',
      back: 'Back',
      saving: 'Saving...',
    }
  }[language];

  return (
    <div className="min-h-screen bg-[#F9F8F6] pt-24 pb-16" data-testid="profile-page">
      <div className="max-w-2xl mx-auto px-6">
        
        {/* Aviso de Redirecionamento/Perfil Incompleto */}
        {showIncompleteWarning && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-3 animate-in fade-in slide-in-from-top-4 duration-500">
            <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-amber-800 font-medium">{t.incompleteTitle}</h4>
              <p className="text-amber-700 text-sm mt-1">{t.incompleteMsg}</p>
            </div>
          </div>
        )}

        <div className="mb-8 animate-fade-in-up">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4 text-[#635E54] hover:text-[#1A1815]"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t.back}
          </Button>
          <h1 className="font-heading text-3xl md:text-4xl font-medium text-[#1A1815]">
            {t.title}
          </h1>
          <p className="text-[#635E54] mt-2">{t.subtitle}</p>
        </div>

        {/* Profile Avatar */}
        <div className="flex justify-center mb-8 animate-fade-in-up">
          <div className="relative">
            {user?.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-24 h-24 rounded-full object-cover border-4 border-[#BFA05F]"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-[#BFA05F] flex items-center justify-center text-white text-3xl font-medium">
                {user?.name?.charAt(0) || 'U'}
              </div>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 animate-fade-in-up">
          <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center gap-2 text-[#1A1815]">
                <User className="w-4 h-4 text-[#BFA05F]" />
                {t.name}
              </Label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleInputChange}
                className="border-[#E6E2DA] focus:border-[#BFA05F] rounded-xl"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2 text-[#1A1815]">
                <Mail className="w-4 h-4 text-[#BFA05F]" />
                {t.email}
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                disabled
                className="border-[#E6E2DA] bg-[#F9F8F6] rounded-xl text-[#635E54]"
              />
            </div>

            {/* NOVO: Birthdate */}
            <div className="space-y-2">
              <Label htmlFor="birthdate" className="flex items-center gap-2 text-[#1A1815]">
                <Calendar className="w-4 h-4 text-[#BFA05F]" />
                {t.birthdate}
              </Label>
              <Input
                id="birthdate"
                name="birthdate"
                type="date"
                value={formData.birthdate}
                onChange={handleInputChange}
                className="border-[#E6E2DA] focus:border-[#BFA05F] rounded-xl"
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-[#1A1815]">
                📱 {t.phone}
              </Label>
              <PhoneInput
                international
                defaultCountry="PT"
                value={formData.phone}
                onChange={(value) => setFormData(prev => ({ ...prev, phone: value || '' }))}
                className="phone-input-custom"
              />
              <p className="text-sm text-[#635E54] flex items-center gap-1">
                <CheckCircle className="w-3 h-3 text-green-500" />
                {t.phoneHelper}
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1A1815] text-white hover:bg-[#2C2924] rounded-xl py-6"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? t.saving : t.save}
            </Button>
          </div>
        </form>

        {/* Password Section */}
        {user?.auth_provider === 'email' && (
          <div className="mt-6 animate-fade-in-up">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              {!showPasswordForm ? (
                <Button
                  variant="outline"
                  onClick={() => setShowPasswordForm(true)}
                  className="w-full border-[#E6E2DA] hover:border-[#BFA05F] rounded-xl"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  {t.changePassword}
                </Button>
              ) : (
                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                  <h3 className="font-medium text-[#1A1815] flex items-center gap-2">
                    <Lock className="w-4 h-4 text-[#BFA05F]" />
                    {t.changePassword}
                  </h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">{t.currentPassword}</Label>
                    <Input
                      id="currentPassword"
                      name="currentPassword"
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={handleInputChange}
                      className="border-[#E6E2DA] focus:border-[#BFA05F] rounded-xl"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">{t.newPassword}</Label>
                    <Input
                      id="newPassword"
                      name="newPassword"
                      type="password"
                      value={passwordData.newPassword}
                      onChange={handleInputChange}
                      className="border-[#E6E2DA] focus:border-[#BFA05F] rounded-xl"
                    />
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowPasswordForm(false)}
                      className="flex-1 border-[#E6E2DA] rounded-xl"
                    >
                      {t.cancel}
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-[#BFA05F] text-white hover:bg-[#9E8246] rounded-xl"
                    >
                      {loading ? t.saving : t.save}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        <div className="mt-8">
          <NotificationPreferences />
        </div>
      </div>
      <style>{`
        .phone-input-custom {
          display: flex;
          align-items: center;
          border: 1px solid #E6E2DA;
          border-radius: 12px;
          padding: 8px 12px;
          background: white;
        }
        .phone-input-custom:focus-within {
          border-color: #BFA05F;
          outline: none;
        }
        .phone-input-custom .PhoneInputInput {
          border: none;
          outline: none;
          flex: 1;
          font-size: 16px;
        }
      `}</style>
    </div>
  );
};

export default ProfilePage;